import { useMap } from "@vis.gl/react-maplibre";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { DRAW_POLYLINE_MIN_POINTS, getAliveMap } from "@ryde/controls";
import {
  HALF_FACTOR,
  latLngTuplesToLineStringFeature,
  Log,
  TLatLngTuple,
} from "@ryde/shared";

import { MIN_LINE_POINTS } from "../route-block-maplibre.constants";
import { simplifyEditPositions } from "./maplibre-editable-polyline.utils";
import {
  IEditMidpoint,
  IUseMapLibreEditablePolylineResult,
} from "./use-maplibre-editable-polyline.models";

interface IUseMapLibreEditablePolylineParams {
  positions: TLatLngTuple[];
  isDeletePointMode: boolean;
  onChange: (positions: TLatLngTuple[]) => void;
}

/**
 * Editing state + geometry math for the in-place polyline editor: seeds simplified handles from the saved
 * route, then applies vertex drag / delete and midpoint insert. Kept out of the component so it renders
 * only the returned geometry and handlers.
 */
export const useMapLibreEditablePolyline = ({
  positions,
  isDeletePointMode,
  onChange,
}: IUseMapLibreEditablePolylineParams): IUseMapLibreEditablePolylineResult => {
  const { current: mapRef } = useMap();
  const { t } = useTranslation();

  const [editPositions, setEditPositions] = useState<TLatLngTuple[]>(() => {
    const map = getAliveMap(mapRef);
    return map ? simplifyEditPositions(map, positions) : positions;
  });
  const editPositionsRef = useRef(editPositions);
  editPositionsRef.current = editPositions;

  const applyPositions = useEvent(
    (next: TLatLngTuple[], commit: boolean): void => {
      editPositionsRef.current = next;
      setEditPositions(next);
      if (commit) {
        onChange(next);
      }
    },
  );

  const handleVertexDrag = useEvent(
    (index: number, lat: number, lng: number, commit: boolean): void => {
      const next = [...editPositionsRef.current];
      next[index] = [lat, lng];
      applyPositions(next, commit);
    },
  );

  const handleVertexDelete = useEvent((index: number): void => {
    if (editPositionsRef.current.length <= DRAW_POLYLINE_MIN_POINTS) {
      Log.warnAndToast(
        t("errors.CANNOT_DELETE_MIN_POINTS", { min: DRAW_POLYLINE_MIN_POINTS }),
      );
      return;
    }
    applyPositions(
      editPositionsRef.current.filter((_, i) => i !== index),
      true,
    );
  });

  const handleMidpointInsert = useEvent(
    (index: number, lat: number, lng: number): void => {
      const next = [...editPositionsRef.current];
      next.splice(index + 1, 0, [lat, lng]);
      applyPositions(next, true);
    },
  );

  const lineFeature = useMemo(
    () => latLngTuplesToLineStringFeature(editPositions),
    [editPositions],
  );

  const midpoints = useMemo<IEditMidpoint[]>(() => {
    if (isDeletePointMode) {
      return [];
    }

    return editPositions.slice(0, -1).map(([lat, lng], index) => {
      const [nextLat, nextLng] = editPositions[index + 1];
      return {
        index,
        lat: (lat + nextLat) / HALF_FACTOR,
        lng: (lng + nextLng) / HALF_FACTOR,
      };
    });
  }, [editPositions, isDeletePointMode]);

  return {
    editPositions,
    midpoints,
    lineFeature,
    hasDrawableLine: editPositions.length >= MIN_LINE_POINTS,
    handleVertexDrag,
    handleVertexDelete,
    handleMidpointInsert,
  };
};
